import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { TranslateService } from './translate.service';
import { ChatMessage } from './chat-ai.service';
import { TutorContext } from './tutor-context-state.service';
import { environment } from '@env/environment';

export interface TutorSuggestedQuestion {
    id: string;
    text: string;
    isStatic: boolean;
}

export interface TutorMessage extends ChatMessage {
    suggestedQuestions?: TutorSuggestedQuestion[];
}

interface TutorSuggestionsApiResponse {
    success: boolean;
    data: {
        suggestions: Array<{ text: string; category: string }>;
    };
}

interface TutorAskApiResponse {
    success: boolean;
    data: {
        answer: string;
        followUpQuestions: Array<{ text: string }>;
    };
}

export interface TutorAskResult {
    answer: string;
    followUpQuestions: TutorSuggestedQuestion[];
}

@Injectable()
export class TutorAiService {
    private readonly USE_MOCK = false; // Cambiar a false cuando el backend esté listo

    // Espejo de los límites de TutorAskRequest/TutorSuggestionsRequest (api-2). El backend también
    // recorta, pero recortamos aquí para no enviar payloads inútilmente grandes: las descripciones
    // del catálogo pasan de 2.000 caracteres con facilidad y el tutor solo usa los primeros 2.000.
    private readonly LIMIT_TEXT = 2000;
    private readonly LIMIT_TITLE = 500;
    private readonly LIMIT_QUESTION = 1000;

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private translateService: TranslateService,
    ) { }

    getStaticQuestions(): TutorSuggestedQuestion[] {
        return [
            { id: 'static-1', text: this.translateService.instant('chat.tutor.static-q1'), isStatic: true },
            { id: 'static-2', text: this.translateService.instant('chat.tutor.static-q2'), isStatic: true },
            { id: 'static-3', text: this.translateService.instant('chat.tutor.static-q3'), isStatic: true },
        ];
    }

    getSuggestions(ctx: TutorContext): Observable<TutorSuggestedQuestion[]> {
        const staticQuestions = this.getStaticQuestions();

        if (this.USE_MOCK) {
            return of([
                ...staticQuestions,
                { id: 'dynamic-1', text: `¿Cuáles son los requisitos previos para "${ctx.contentTitle}"?`, isStatic: false },
                { id: 'dynamic-2', text: '¿Qué habilidades desarrollaré con este contenido?', isStatic: false },
            ]);
        }

        const companyId = this.storageService.get('company')?.id;
        const language = this.storageService.get('me')?.lang || 'es';
        const apiUrl = `${environment.envVar.API_URL}/company/${companyId}/ai/tutor/suggestions`;

        const payload = {
            content_type: ctx.contentType,
            content_id: ctx.contentId,
            content_title: this.truncate(ctx.contentTitle, this.LIMIT_TITLE),
            content_description: this.truncate(this.stripHtml(ctx.contentDescription), this.LIMIT_TEXT) || undefined,
            section_title: this.truncate(ctx.sectionTitle, this.LIMIT_TITLE),
            section_description: this.truncate(ctx.sectionDescription, this.LIMIT_TEXT),
            activity_title: this.truncate(ctx.activityTitle, this.LIMIT_TITLE) || undefined,
            activity_description: ctx.activityDescription
                ? this.truncate(this.stripHtml(ctx.activityDescription), this.LIMIT_TEXT)
                : undefined,
            language,
            num_suggestions: 2,
        };

        return this.http.post<TutorSuggestionsApiResponse>(apiUrl, payload).pipe(
            map(response => {
                const dynamic = (response.data?.suggestions || []).map((s, i) => ({
                    id: `dynamic-${i}`,
                    text: this.decodeHtmlEntities(s.text),
                    isStatic: false,
                }));
                return [...staticQuestions, ...dynamic];
            }),
            catchError(() => of(staticQuestions)),
        );
    }

    askQuestion(
        question: string,
        ctx: TutorContext,
        history: TutorMessage[],
    ): Observable<TutorAskResult> {
        if (this.USE_MOCK) {
            return of({
                answer: `Esta es una respuesta de ejemplo sobre <strong>"${ctx.contentTitle}"</strong>.<br><br>
                La pregunta que hiciste fue: <em>${question}</em>.<br><br>
                Cuando el backend esté listo, aquí aparecerá la respuesta real del tutor de IA contextualizada al contenido que estás estudiando.`,
                followUpQuestions: [
                    { id: 'follow-1', text: '¿Puedes profundizar más en este tema?', isStatic: false },
                    { id: 'follow-2', text: '¿Hay ejercicios prácticos relacionados?', isStatic: false },
                    { id: 'follow-3', text: '¿Qué recursos adicionales recomiendas?', isStatic: false },
                ],
            });
        }

        const companyId = this.storageService.get('company')?.id;
        const language = this.storageService.get('me')?.lang || 'es';
        const apiUrl = `${environment.envVar.API_URL}/company/${companyId}/ai/tutor/ask`;

        // Las respuestas del tutor pueden llegar a 6-8k caracteres (max_tokens 2048): sin recorte,
        // el primer turno largo dejaba el resto de la conversación devolviendo 400.
        const conversationHistory = history.slice(-10).map(m => ({
            role: m.role,
            content: this.truncate(m.content, this.LIMIT_TEXT),
        }));

        const payload = {
            question: this.truncate(question, this.LIMIT_QUESTION),
            content_type: ctx.contentType,
            content_id: ctx.contentId,
            content_title: this.truncate(ctx.contentTitle, this.LIMIT_TITLE),
            content_description: this.truncate(this.stripHtml(ctx.contentDescription), this.LIMIT_TEXT) || undefined,
            section_title: this.truncate(ctx.sectionTitle, this.LIMIT_TITLE),
            section_description: this.truncate(ctx.sectionDescription, this.LIMIT_TEXT),
            activity_title: this.truncate(ctx.activityTitle, this.LIMIT_TITLE) || undefined,
            activity_description: ctx.activityDescription
                ? this.truncate(this.stripHtml(ctx.activityDescription), this.LIMIT_TEXT)
                : undefined,
            language,
            conversation_history: conversationHistory,
        };

        return this.http.post<TutorAskApiResponse>(apiUrl, payload).pipe(
            map(response => ({
                answer: response.data.answer,
                followUpQuestions: (response.data.followUpQuestions || []).map((q, i) => ({
                    id: `follow-${i}`,
                    text: this.decodeHtmlEntities(q.text),
                    isStatic: false,
                })),
            })),
        );
    }

    private truncate(text: string | undefined, limit: number): string | undefined {
        if (!text) return text;
        return text.length > limit ? text.slice(0, limit) : text;
    }

    private stripHtml(text: string): string {
        if (!text) return text;
        const div = document.createElement('div');
        div.innerHTML = text;
        return div.textContent || div.innerText || text;
    }

    private decodeHtmlEntities(text: string): string {
        if (!text) return text;
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    }
}
